/* Hook-free: the portal renders the same timeline from its own source. */

import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { Expander, Notice, Timeline, type TimelineItem } from "../../../../design/components";
import { dateTime } from "../../../../lib/format";
import { IN_STAGE_LABEL, STAGE_LABEL } from "../../../../domain/stages";
import type { DocumentEvent } from "../../../../api/types";

/** Read per call: a spread would copy the localized values once, at import time. Outbound
    names take precedence over inbound ones for the stages they share (`mapped`, `validated`,
    `archived`), exactly as the original `{ ...IN_STAGE_LABEL, ...STAGE_LABEL }` did. */
const stageName = (stage: string): string | undefined =>
  (STAGE_LABEL as Record<string, string>)[stage] ?? (IN_STAGE_LABEL as Record<string, string>)[stage];

/** Array.sort is stable, so events sharing a timestamp keep the API's order. */
export function sortEvents(events: DocumentEvent[]): DocumentEvent[] {
  return [...events].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

function detailBody(detail: DocumentEvent["detail"]) {
  if (detail === null || detail === undefined) return null;
  if (typeof detail === "string") return <span>{detail}</span>;
  return <pre className="code">{JSON.stringify(detail, null, 2)}</pre>;
}

const FAIL_STAGES = new Set(["error", "rejected"]);
const HOLD_STAGES = new Set(["held_unrouted", "held_exception"]);
const FAIL_STATUSES = new Set(["ERROR", "FAILED_VALIDATION", "REJECTED"]);

function failed(event: DocumentEvent): boolean {
  if (FAIL_STAGES.has(event.stage)) return true;
  const detail = event.detail;
  if (!detail || typeof detail !== "object") return false;
  const { error_code: code, legacy_status: status } = detail as Record<string, unknown>;
  return Boolean(code) || FAIL_STATUSES.has(String(status));
}

/** Every recorded event has happened, so it is filled; the newest one is marked as current. */
export function eventState(event: DocumentEvent, isLast: boolean): NonNullable<TimelineItem["state"]> {
  if (failed(event)) return "fail";
  if (HOLD_STAGES.has(event.stage)) return "warn";
  return isLast ? "now" : "done";
}

function toItem(event: DocumentEvent, index: number, all: DocumentEvent[], detailLabel: string): TimelineItem {
  const body = detailBody(event.detail);
  return {
    title: stageName(event.stage) ?? event.stage,
    state: eventState(event, index === all.length - 1),
    at: `${dateTime(event.createdAt)}${event.actor ? ` · ${event.actor}` : ""}`,
    ...(body ? { detail: <Expander head={detailLabel} body={body} /> } : {}),
  };
}

/** The body alone, so the portal can feed it from its own source. */
export function EventsBody({ events, isError }: { events: DocumentEvent[] | undefined; isError: boolean }) {
  const { t } = useTranslation();
  if (isError) return <Notice kind="fail">{t("documents.inspector.events.loadError", "The event history could not be loaded.")}</Notice>;
  if (events && events.length === 0) {
    return <Notice kind="info">{t("documents.inspector.events.empty", "No events recorded yet.")}</Notice>;
  }
  const detailLabel = t("documents.inspector.events.detail", "Detail");
  return <Timeline items={sortEvents(events ?? []).map((event, index, all) => toItem(event, index, all, detailLabel))} />;
}

